// Bagged-peak celebrations drawn over the flyover video, frame by frame.
//
// Every summit the walk bagged gets the app's own peak puck (slate until the flyover reaches it,
// green after), and on arrival: the pin pops, a pulse ring spreads across the ground, yellow and
// white confetti bursts, and a card pops up on the summit naming it. The same celebration the
// in-app 3D flyover plays — the timing and shapes all come from flyoverPeaks.ts.
//
// Shared by the video renderer (lib/flyover_renderer/entry.ts) and the live 3D preview on the
// recording page (apps/flyover_preview/FlyoverPreview.vue), so the two can never drift apart.
//
// Drawn as DOM over the map canvas rather than as map layers. The renderer positions it after each
// frame has settled and before the screenshot, so every captured frame is pixel-locked to the
// terrain, on the clip clock so a render is reproducible. The live preview positions it from the
// map's own `render` event — the same frame as the GL draw, which is how mapboxgl.Marker stays put —
// on wall time.

import {
  FlyoverPeakConfig, PlacedPeak, PeakArrivals, placePeaks, pinScale, pulses, cardScale,
  confetti, hashSeed, peakCardText, dwellStretch, CONFETTI_MS, chooseCardSide, cardRect, Bounds, CardSide, Pt
} from "./flyoverPeaks";

const FONT = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const YELLOW = "#FDDF00";
const CARD_BG = "rgba(23,24,29,0.93)";

// The app's two peak pucks (Android puck_icon_peak / puck_icon_peak_unbagged), so a summit in the
// video is the same pin the walker sees on every map in the app.
function puckSvg(bagged: boolean): string {
  const disc = bagged ? "#95A629" : "#575E73";
  return `<svg viewBox="0 0 85 85" width="100%" height="100%" style="display:block;overflow:visible;filter:drop-shadow(0 2px 4px rgba(0,0,0,.45))">` +
    `<circle cx="42.6" cy="42.8" r="41.94" fill="${disc}"/>` +
    `<circle cx="42.6" cy="42.8" r="40.44" fill="none" stroke="#fff" stroke-width="3"/>` +
    `<path d="M51.001,58.391H16.65L33.826,33.027L51.001,58.391Z" fill="#fff" stroke="#95A629" stroke-width="3"/>` +
    `<path d="M68.576,58.39H26.037L47.207,26.836L68.576,58.39Z" fill="#fff" stroke="#95A629" stroke-width="3"/></svg>`;
}

// The same stand-in every celebration uses for a peak on no list: a yellow disc with the mountain.
const FALLBACK_BADGE =
  `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block"><circle cx="12" cy="12" r="12" fill="${YELLOW}"/>` +
  `<path transform="translate(4.9 4.9) scale(0.59)" d="M14,6l-3.75,5 2.85,3.8 -1.6,1.2C9.81,13.75 7,10 7,10l-6,8h22L14,6z" fill="#000"/></svg>`;

export interface PeakOverlay {
  /** Where the bagged summits sit on the route (0..1), for the playback to ease into. */
  fracs: number[];
  /** How much the summit dwells lengthen the body, so the 45s fit can allow for them. */
  stretch: number;
  /** How many summits there are to bag. */
  total: number;
  /** Waits for the list badges, so no captured frame shows a card before its badge. */
  preload(): Promise<void>;
  /**
   * Position and animate everything for this frame. t = body progress (0 before, 1 after); `natural`
   * false for a skip, which plants (or un-plants) summits without celebrating them.
   */
  update(t: number, clipMs: number, natural?: boolean): void;
  /** How many summits the flyover has reached. */
  reachedCount(): number;
  /** Whether a celebration is still animating — the live preview keeps repainting until it ends. */
  isAnimating(clipMs: number): boolean;
  /** A replay from the start. */
  reset(): void;
  /** Takes everything off the page. */
  destroy(): void;
}

export interface PeakOverlayOptions {
  /** What the overlay is laid over; positions are in its coordinates. Defaults to the page. */
  container?: HTMLElement;
  /** App dp -> px. Defaults to the video's: a ~411dp phone composition at frame resolution. */
  scale?: number;
  /** Device pixels per CSS px for the confetti canvas (the live preview passes devicePixelRatio). */
  pixelRatio?: number;
  zIndex?: number;
}

interface PeakEls {
  pin: HTMLElement;
  pinBagged: boolean;
  name: HTMLElement;
  card: HTMLElement;
  tip: HTMLElement;
  rings: HTMLElement[];
  /** Chosen when the card first appears and held for the whole show, so it never jumps sides. */
  side: CardSide;
  w: number;
  h: number;
}

export function createPeakOverlay(
  map: any,
  peaks: FlyoverPeakConfig[] | null | undefined,
  coords: number[][],
  cumKm: number[],
  formatElevation: (m: number) => string,
  /** The part of the frame a card may use: clear of the logo and the stats panel. */
  safeBounds: () => Bounds,
  options: PeakOverlayOptions = {}
): PeakOverlay | null {
  const placed: PlacedPeak[] = placePeaks(peaks, coords, cumKm);
  if (!placed.length) return null;

  const container = options.container || document.body;
  const size = () => (container === document.body
    ? { w: window.innerWidth, h: window.innerHeight }
    : { w: container.clientWidth, h: container.clientHeight });
  let { w: vw, h: vh } = size();
  // App dp -> px. The video's default: the app's flyover is drawn for a ~411dp-wide phone and the
  // clip is the same composition at frame resolution. The live preview is in CSS px, ~1:1 with dp.
  const k = options.scale ?? Math.min(vw, vh) / 411;
  const dpr = options.pixelRatio || 1;
  const px = (dp: number) => `${(dp * k).toFixed(1)}px`;
  const pinPx = 27.6 * k;              // 85dp puck at the app's flyover icon size (0.25 x 1.3)
  const ringMax = 38 * k;              // pulse spread
  const tipLen = 6 * k;                // the card's pointer
  const cardGap = 17 * k + tipLen;     // summit to the card's edge: the pin, then the pointer

  const root = document.createElement("div");
  root.style.cssText = `position:absolute;inset:0;z-index:${options.zIndex ?? 9};pointer-events:none;overflow:hidden`;
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "position:absolute;left:0;top:0;width:100%;height:100%;z-index:2";
  const ctx = canvas.getContext("2d")!;
  const sizeCanvas = () => {
    canvas.width = Math.round(vw * dpr); canvas.height = Math.round(vh * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  sizeCanvas();
  container.appendChild(root);

  const arrivals = new PeakArrivals(placed);
  const badgeLoads: Promise<void>[] = [];

  const els: PeakEls[] = placed.map(({ peak }) => {
    const accent = peak.accent || YELLOW;
    const rings = [0, 1].map(() => {
      const r = document.createElement("div");
      r.style.cssText = `position:absolute;left:0;top:0;border:${px(2.5)} solid ${accent};border-radius:50%;display:none;z-index:0`;
      root.appendChild(r);
      return r;
    });

    const pin = document.createElement("div");
    pin.style.cssText = `position:absolute;left:0;top:0;width:${pinPx}px;height:${pinPx}px;z-index:1;display:none;transform-origin:50% 50%`;
    pin.innerHTML = puckSvg(false);
    root.appendChild(pin);

    const name = document.createElement("div");
    name.textContent = peak.name || "";
    name.style.cssText = `position:absolute;left:0;top:0;z-index:1;display:none;white-space:nowrap;font:700 ${px(10)} ${FONT};` +
      `color:#fff;text-shadow:0 0 ${px(2)} #000,0 0 ${px(2)} #000,0 1px ${px(3)} rgba(0,0,0,.8)`;
    root.appendChild(name);

    const { card, tip } = buildCard(peak, accent, formatElevation, px, badgeLoads);
    root.appendChild(card);
    return { pin, pinBagged: false, name, card, tip, rings, side: "above" as CardSide, w: 0, h: 0 };
  });
  root.appendChild(canvas);

  const fracs = placed.map((p) => p.frac);
  return {
    fracs,
    stretch: dwellStretch(fracs),
    total: placed.length,
    async preload() {
      await Promise.race([Promise.all(badgeLoads), new Promise((r) => setTimeout(r, 8000))]);
    },
    reachedCount: () => arrivals.reachedCount(),
    isAnimating: (clipMs: number) => arrivals.isAnimating(clipMs),
    reset: () => arrivals.reset(),
    destroy: () => root.remove(),
    update(t: number, clipMs: number, natural = true) {
      const now = size();
      if (now.w !== vw || now.h !== vh) { vw = now.w; vh = now.h; sizeCanvas(); }
      const started = arrivals.update(t, clipMs, natural);
      if (started.length) {
        // Where the line is on screen right now, once per arrival: the card goes wherever it
        // hides the least of it.
        const route: Pt[] = coords.map((c) => map.project([c[0], c[1]]));
        const bounds = safeBounds();
        for (const a of started) {
          const e = els[a.index];
          const pt = map.project([placed[a.index].peak.lng, placed[a.index].peak.lat]);
          measureCard(e);
          e.side = chooseCardSide(pt, e.w, e.h, cardGap, route, bounds);
          aimTip(e.tip, e.side, tipLen);
        }
      }
      ctx.clearRect(0, 0, vw, vh);
      placed.forEach((p, i) => {
        const e = els[i];
        const pt = map.project([p.peak.lng, p.peak.lat]);
        const onScreen = Number.isFinite(pt.x) && Number.isFinite(pt.y) &&
          pt.x > -vw * 0.25 && pt.x < vw * 1.25 && pt.y > -vh * 0.25 && pt.y < vh * 1.25;
        const reached = arrivals.isReached(i);
        const active = arrivals.activeFor(i, clipMs);
        const elapsed = active ? clipMs - active.atMs : -1;

        if (!onScreen) {
          e.pin.style.display = e.name.style.display = e.card.style.display = "none";
          e.rings.forEach((r) => { r.style.display = "none"; });
          return;
        }

        if (reached !== e.pinBagged) { e.pin.innerHTML = puckSvg(reached); e.pinBagged = reached; }
        const s = active ? pinScale(elapsed) : 1;
        e.pin.style.display = "block";
        e.pin.style.transform = `translate(${pt.x - pinPx / 2}px,${pt.y - pinPx / 2}px) scale(${s})`;

        // The card: popped up from the pin, held, faded — and handed over early when the next
        // summit's card arrives, so a ridge of tops never stacks card on card.
        const op = arrivals.cardOpacityFor(i, clipMs);

        // Reached summits keep their name under the pin — except while their card says it.
        const named = reached && op <= 0;
        e.name.style.display = named ? "block" : "none";
        if (named) e.name.style.transform = `translate(-50%,0) translate(${pt.x}px,${pt.y + pinPx / 2 + 4 * k}px)`;

        // Pulse rings, flat on the ground (an ellipse, as a circle seen from the flyover's pitch).
        const live = active ? pulses(elapsed, active.tier) : [];
        e.rings.forEach((r, ring) => {
          const pulse = live.find((l) => l.ring === ring);
          if (!pulse) { r.style.display = "none"; return; }
          const rx = pinPx / 2 + (ringMax - pinPx / 2) * pulse.radius;
          const ry = rx * 0.55;
          r.style.display = "block";
          r.style.opacity = String(pulse.opacity);
          r.style.width = `${rx * 2}px`; r.style.height = `${ry * 2}px`;
          r.style.transform = `translate(${pt.x - rx}px,${pt.y - ry}px)`;
        });

        if (op <= 0) { e.card.style.display = "none"; } else {
          e.card.style.display = "block";
          e.card.style.opacity = String(op);
          const r = cardRect(e.side, pt, e.w, e.h, cardGap);
          e.card.style.transformOrigin = tipOrigin(e.side, e.w, e.h, tipLen);
          e.card.style.transform = `translate(${r.x}px,${r.y}px) scale(${cardScale(elapsed)})`;
        }

        // Confetti: thrown up from the summit, falling back.
        if (active && elapsed < CONFETTI_MS) {
          for (const bit of confetti(elapsed, hashSeed(p.peak.id))) {
            ctx.save();
            ctx.globalAlpha = bit.opacity;
            ctx.translate(pt.x + bit.dx * k, pt.y + bit.dy * k);
            ctx.rotate((bit.rotation * Math.PI) / 180);
            ctx.fillStyle = bit.yellow ? YELLOW : "#FFFFFF";
            ctx.fillRect(-2.5 * k, -1.3 * k, 5 * k, 2.6 * k);
            ctx.restore();
          }
        }
      });
    }
  };
}

function buildCard(
  peak: FlyoverPeakConfig,
  accent: string,
  formatElevation: (m: number) => string,
  px: (dp: number) => string,
  badgeLoads: Promise<void>[]
): { card: HTMLElement; tip: HTMLElement } {
  const text = peakCardText(peak, formatElevation);
  const card = document.createElement("div");
  // transform-origin is set per side to the pointer tip, so the pop grows the card out of the pin.
  card.style.cssText = `position:absolute;left:0;top:0;z-index:3;display:none;font-family:${FONT}`;

  const body = document.createElement("div");
  body.style.cssText = `display:flex;align-items:center;gap:${px(6)};padding:${px(6)} ${px(8)};background:${CARD_BG};` +
    `border-radius:${px(8)};box-shadow:0 ${px(2)} ${px(8)} rgba(0,0,0,.45);white-space:nowrap`;

  const badge = document.createElement("div");
  badge.style.cssText = `width:${px(22)};height:${px(22)};flex:none;border-radius:50%;overflow:hidden`;
  badge.innerHTML = FALLBACK_BADGE;
  if (peak.badgeUrl) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    badgeLoads.push(new Promise<void>((resolve) => {
      img.onload = () => { badge.innerHTML = ""; img.style.cssText = "width:100%;height:100%;display:block"; badge.appendChild(img); resolve(); };
      img.onerror = () => resolve();
    }));
    img.src = peak.badgeUrl;
  }

  const col = document.createElement("div");
  const name = document.createElement("div");
  name.textContent = text.name;
  name.style.cssText = `font-size:${px(11)};font-weight:700;color:#fff;line-height:1.25;max-width:${px(170)};overflow:hidden;text-overflow:ellipsis`;
  col.appendChild(name);
  if (text.elevation || text.listName) {
    const line = document.createElement("div");
    line.style.cssText = `font-size:${px(9)};line-height:1.3;margin-top:${px(1)}`;
    const parts: string[] = [];
    if (text.elevation) parts.push(`<span style="color:${YELLOW};font-weight:700">${escapeHtml(text.elevation)}</span>`);
    if (text.listName) {
      parts.push(`<span style="color:${accent}">${text.elevation ? " · " : ""}${escapeHtml(text.listName)} </span>`);
      parts.push(`<span style="color:#fff;font-weight:700">${escapeHtml(text.listProgress || "")}</span>`);
    }
    line.innerHTML = parts.join("");
    col.appendChild(line);
  }
  body.append(badge, col);

  // The pointer, aimed at the summit from whichever side the card ends up on (aimTip).
  const tip = document.createElement("div");
  tip.style.cssText = "position:absolute;width:0;height:0";

  card.append(body, tip);
  return { card, tip };
}

/** The card's own size, unscaled — read once it has its badge and text. */
function measureCard(e: PeakEls) {
  const wasHidden = e.card.style.display === "none";
  if (wasHidden) { e.card.style.visibility = "hidden"; e.card.style.display = "block"; }
  e.w = e.card.offsetWidth;
  e.h = e.card.offsetHeight;
  if (wasHidden) { e.card.style.display = "none"; e.card.style.visibility = ""; }
}

/** Puts the pointer on the edge facing the summit. */
function aimTip(tip: HTMLElement, side: CardSide, len: number) {
  const solid = `${len}px solid ${CARD_BG}`;
  const clear = `${len}px solid transparent`;
  tip.style.border = "0";
  tip.style.left = tip.style.top = "";
  switch (side) {
    case "above":
      Object.assign(tip.style, { left: "50%", top: "100%", marginLeft: `${-len}px`, marginTop: "0", borderLeft: clear, borderRight: clear, borderTop: solid });
      break;
    case "below":
      Object.assign(tip.style, { left: "50%", top: `${-len}px`, marginLeft: `${-len}px`, marginTop: "0", borderLeft: clear, borderRight: clear, borderBottom: solid });
      break;
    case "left":
      Object.assign(tip.style, { left: "100%", top: "50%", marginLeft: "0", marginTop: `${-len}px`, borderTop: clear, borderBottom: clear, borderLeft: solid });
      break;
    case "right":
      Object.assign(tip.style, { left: `${-len}px`, top: "50%", marginLeft: "0", marginTop: `${-len}px`, borderTop: clear, borderBottom: clear, borderRight: solid });
      break;
  }
}

/** The pointer's tip, as a transform-origin: the pop grows the card out of the pin. */
function tipOrigin(side: CardSide, w: number, h: number, len: number): string {
  switch (side) {
    case "above": return `${w / 2}px ${h + len}px`;
    case "below": return `${w / 2}px ${-len}px`;
    case "left": return `${w + len}px ${h / 2}px`;
    case "right": return `${-len}px ${h / 2}px`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}
